import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {useSalaryAccess} from '@/entities/permission/access';
import {fetchCurrentSalaries} from '@/entities/salary/api';
import type {PersonnelRecord} from '@/entities/personnel/types';
import {formatMoney} from '@/entities/salary/money';
import {Cell, type Column} from '@/shared/ui/table';
import {useTheme} from '@/shared/ui/theme';

/**
 * Колонка «Оклад» для списка персонала — одним запросом на весь список.
 *
 * ## Почему один запрос, а не запрос на человека
 *
 * `GET /control/salaries` отдаёт по одной ДЕЙСТВУЮЩЕЙ записи на каждого, у кого она
 * есть. Список персонала — ровно тот случай, ради которого этот маршрут и появился:
 * страница из двадцати человек стоила бы двадцати запросов, а стоит одного.
 *
 * ## Почему прочерк, а не ноль
 *
 * ⚠️ **У кого ставки нет — строки в ответе нет вовсе.** Не строка с нулём. Ноль в
 * колонке «Оклад» означал бы «работает бесплатно», и это была бы неправда о конкретном
 * человеке; прочерк означает «не задан», что и есть факт — и что ведомость потом
 * повторит флагом `noSalary`.
 *
 * ## Почему колонка возвращается пустым массивом, а не прячется вызывающим
 *
 * Право проверяется здесь, рядом с запросом, а не в экране: тогда «колонку видно» и
 * «данные запрошены» — одно решение в одном месте, и невозможно оставить включённым
 * одно без другого. Без `salaries/read` хук не спрашивает ничего и не отдаёт колонки.
 */
export function useSalaryColumn(companyId: string): Column<PersonnelRecord>[] {
  const {t} = useTranslation();
  const p = useTheme();
  const access = useSalaryAccess(companyId);

  const salaries = useQuery({
    queryKey: ['salary', 'current', companyId],
    queryFn: () => fetchCurrentSalaries(companyId),
    enabled: access.canRead,
  });

  if (!access.canRead) {
    return [];
  }

  const byRecord = new Map(
    (salaries.data ?? []).map(record => [record.record_id, record]),
  );

  return [
    {
      key: 'salary',
      title: t('salary.column'),
      width: 160,
      align: 'right',
      /**
       * Первая колонка, которая уходит, когда окна не хватает: оклад читают редко, а
       * имя и отдел — всегда.
       *
       * Порог высокий (`1600`, не `1280`) потому, что `minWidth` сравнивается с шириной
       * ОКНА, а таблица персонала делит его с панелью карточки — на 1440pt ей достаётся
       * около 790, и шестая колонка на 160pt отбирала их у имени: «Юлдашева Нил…».
       */
      minWidth: 1600,
      render: row => {
        const record = byRecord.get(row.record_id);
        return (
          <Cell tone={record ? p.foreground : p.mutedForeground}>
            {record ? formatMoney(record.amount_minor, record.currency) : '—'}
          </Cell>
        );
      },
    },
  ];
}
